<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import ToggleInputDefault from "@/components/controls/ToggleInputDefault.vue";
  import Icon from "@/components/icons/Icon.vue";

  defineEmits(['input:change']);

  // The selector's shared selection (array in multi-select, scalar otherwise).
  const selected = defineModel();

  const props = defineProps({
    model: {
      type: Object,
      required: true
    },
    // Input group name (shared across the selector's items).
    name: {
      type: String,
      required: true
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    unavailable: {
      type: Boolean,
      default: false
    },
    // Model IDs offered as a free preview (sourced from ForecastModelStore).
    freePreviewModels: {
      type: Array,
      default: () => []
    },
    freePreviewAllMemberLevelsModels: {
      type: Array,
      default: () => []
    }
  });

  const { t } = useI18n();

  const inputId = computed(() => `${props.name}-${props.model.model_id}`);

  const hasFreePreview = computed(() => props.freePreviewModels.includes(props.model.model_id));
  const hasFreePreviewAllMemberLevels = computed(() => props.freePreviewAllMemberLevelsModels.includes(props.model.model_id));
  const isPreview = computed(() => hasFreePreview.value || hasFreePreviewAllMemberLevels.value);

  const attributes = computed(() => ({
    id: inputId.value,
    value: props.model.model_id,
    inputType: props.multiSelect ? 'checkbox' : 'radio',
    disabled: props.unavailable || props.model.upgrade_available,
  }));

  const isSelected = computed(() =>
    props.multiSelect
      ? Array.isArray(selected.value) && selected.value.includes(props.model.model_id)
      : selected.value === props.model.model_id
  );
</script>

<template>
  <div class="jwx-model-selector__model">
    <ToggleInputDefault
      @input:change="$emit('input:change', $event)"
      :attributes="attributes"
      :name="name"
      :disabled="unavailable || (model.upgrade_available && !hasFreePreview)"
      v-model="selected"
    />
    <label
      :for="inputId"
      class="jwx-model-selector__label"
      :data-selected="isSelected"
      :data-premium="model.is_premium"
      :data-preview="isPreview"
      :data-unavaible="unavailable"
      :aria-disabled="unavailable"
    >
      <Icon v-if="unavailable" name="exclamation-circle" class="jwx-model-selector__unavailable-icon" />
      <p class="jwx-model-selector__label-text">{{ model.model_short_name || model.model_name }}</p>
      <span
        v-if="hasFreePreview && model.upgrade_available || hasFreePreviewAllMemberLevels"
        class="jwx-model-selector__badge"
        data-type="preview"
      >
        <Icon name="sparkle" />
        {{ t('containers.forecastTable.preview') }}
      </span>
      <span
        v-else-if="model.is_pro_only"
        class="jwx-model-selector__pro-badge"
        :data-upgrade-available="model.upgrade_available"
      >
        {{ t('membership.pro') }}
        <Icon v-if="model.upgrade_available" name="lock" />
      </span>
      <Icon v-else-if="model.upgrade_available" name="lock" />
      <Icon v-else-if="!model.upgrade_available && model.is_premium" name="premium-badge" />
    </label>
  </div>
</template>
